import React from 'react'
import {FAQResponse} from '@findhotel/sapi/dist/types/packages/core/src/types'

import {Box} from '@vio/atlas/helpers/Box'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Icon, type IconName} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

interface BaseSection {
  id: string
  iconName: IconName
  title: React.ReactNode
}

type SectionWithDescription = BaseSection & {
  description: React.ReactNode | string
}

interface OptionalExtraSectionProps extends SectionWithDescription {
  showDivider: boolean
}

const OptionalExtraSection = ({
  iconName,
  title,
  description,
  showDivider
}: OptionalExtraSectionProps) => {
  return (
    <>
      <div>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          gap="s300"
        >
          <Icon name={iconName} size="md" />
          <Text variant="titleM">{title}</Text>
        </Box>

        <Box paddingLeft="s600" paddingTop="s250">
          <Box paddingLeft="s200">
            <Text variant="bodyM" dir="auto">
              {description}
            </Text>
          </Box>
        </Box>
      </div>
      {showDivider && (
        <Box paddingTop="s500" paddingBottom="s500">
          <Divider />
        </Box>
      )}
    </>
  )
}

const sectionTemplates: BaseSection[] = [
  {
    id: 'ExtraBedsCribs',
    iconName: 'SingleBed',
    title: (
      <FormattedMessageWrapper
        id="extraBedsCribs"
        defaultMessage="Cribs and extra beds"
      />
    )
  },
  {
    id: 'KidsFriendliness',
    iconName: 'Child',
    title: (
      <FormattedMessageWrapper
        id="childPolicies"
        defaultMessage="Child policies"
      />
    )
  },
  {
    id: 'Breakfast',
    iconName: 'Restaurant',
    title: (
      <FormattedMessageWrapper id="mealOptions" defaultMessage="Meal options" />
    )
  },
  {
    id: 'PetFriendliness',
    iconName: 'Bone',
    title: (
      <FormattedMessageWrapper id="petPolicies" defaultMessage="Pet policies" />
    )
  }
]

export const OptionalExtrasPolicies = ({faqData}: {faqData: FAQResponse}) => {
  const faqs = faqData?.faqs

  if (!faqs || faqs.length === 0) return null

  const sections: SectionWithDescription[] = sectionTemplates.flatMap(
    section => {
      const matched = faqs.find(
        (item: {label: string; answer: string}) => item.label === section.id
      )
      return matched ? [{...section, description: matched.answer}] : []
    }
  )

  return (
    <>
      <Box paddingBottom="s500">
        <Text variant="titleL">
          <FormattedMessageWrapper
            id="optionalExtras"
            defaultMessage="Optional extras"
          />
        </Text>
      </Box>
      {sections.map((section, index) => (
        <OptionalExtraSection
          key={section.id}
          iconName={section.iconName}
          title={section.title}
          description={section.description}
          showDivider={index !== sections.length - 1}
          id={section.id}
        />
      ))}
    </>
  )
}
